'use client'

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { HmrcTokenExpiredError, useHMRCITSAStore } from '@repo/store/useHMRCITSAStore'
import { useGlobalSettingsStore } from '@repo/store/useGlobalSettingsStore'
import { isValidNI } from '@repo/validation/isValidNI'
import { StepIndicator } from '../StepIndicator/StepIndicator'
import { NINumberField } from '../NINumberField/NINumberField'
import { FiguresReviewTable } from '../FiguresReviewTable/FiguresReviewTable'
import { AccountingBasisPill } from '../AccountingBasisPill/AccountingBasisPill'
import { FraudHeaderValidationToast } from '../FraudHeaderValidationToast/FraudHeaderValidationToast'
import { deriveTaxYear } from '../dateUtils'
import type {
  AccountingBasis,
  Business,
  ItsaBusinessObligation,
  ObligationStatus,
  CalculateSelfEmploymentCumulativePeriodSummaryResponse,
  FraudHeaderValidationSummary
} from '../types'
import {
  DISALLOWABLE_EXPENSE_FIELDS,
  EXPENSE_FIELDS,
  HMRC_SETUP_ROUTE_PATH,
  INCOME_FIELDS,
  OBLIGATION_STATUS_FILTER_OPTIONS,
  buildFigureRows,
  getApiErrorMessage,
  isValidationError
} from './constants'
import MUIAlert from '@mui/material/Alert'
import MUIButton from '@mui/material/Button'
import MUICheckbox from '@mui/material/Checkbox'
import MUICircularProgress from '@mui/material/CircularProgress'
import MUIFormControl from '@mui/material/FormControl'
import MUIFormControlLabel from '@mui/material/FormControlLabel'
import MUIInputLabel from '@mui/material/InputLabel'
import MUIMenuItem from '@mui/material/MenuItem'
import MUIRadio from '@mui/material/Radio'
import MUIRadioGroup from '@mui/material/RadioGroup'
import MUISelect from '@mui/material/Select'
import MUIStack from '@mui/material/Stack'
import MUITypography from '@mui/material/Typography'

const STEP_NI = 0
const STEP_BUSINESS = 1
const STEP_OBLIGATION = 2
const STEP_REVIEW = 3

const STEPS = ['National Insurance number', 'Select business', 'Select obligation', 'Review figures']

const GENERIC_BUSINESSES_ERROR = 'An error occurred while fetching businesses. Please try again.'
const GENERIC_ELIGIBILITY_ERROR = 'An error occurred while checking this business. Please try again.'
const GENERIC_OBLIGATIONS_ERROR = 'An error occurred while fetching obligations. Please try again.'
const GENERIC_CALCULATE_ERROR = 'An error occurred while calculating the summary. Please try again.'
const GENERIC_SUBMIT_ERROR = 'An error occurred while submitting to HMRC. Please try again.'

// FR-08 acknowledgement copy — the checkbox is a client-side UX gate only; the server
// trusts the acknowledgementToken minted by Calculate (BE9), not this flag (ADR 0031).
const ACKNOWLEDGEMENT_TEXT =
  'I confirm that these figures are complete and correct, that the categorisation applied is correct, and I understand that I am responsible for this submission to HMRC.'

// HMRC fraud-prevention-headers Terms of Use: users must be told what device/connection
// data is collected and sent. This checkbox is a NOTICE gate only — the lawful basis for
// sending the headers is legal obligation, not consent, so the tick is deliberately NOT
// stored anywhere (storing it would imply revocable consent semantics that do not apply).
const FRAUD_PREVENTION_NOTICE_TEXT =
  'I understand that Osprey collects information about my device and connection (device identifier, screen and window details, timezone, browser details and IP address) and sends it to HMRC with this submission, as required by UK law for fraud prevention.'

// Required cash-basis warning (functional spec §11 / frontend guide "Known limitations"):
// on the cash basis, unpaid Osprey purchase-ledger supplier invoices are not deductible
// until paid, so they are excluded from the calculated figures — only payments actually
// made in the period are counted.
const CASH_BASIS_WARNING =
  'You are filing on the cash basis. Unpaid purchase-ledger supplier invoices are not deductible until they are paid, so they are not included in these figures — only supplier payments actually made during the period are counted.'

const optionBoxSx = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: '6px',
  padding: '8px 16px',
  marginBottom: 1,
  marginLeft: 0,
  width: '100%'
}

// The quarterly submission wizard (FE7a: NI entry -> business select -> obligation select ->
// calculate -> read-only review; FE7b picks up from the populated review step to add the
// acknowledgement + submit-to-HMRC action). Route registration is owned by FE11 — this
// follows the path ReportModule.tsx already reserves for it (NEW_SUBMISSION_ROUTE_PATH).
// Prototype UI direction: johntaylor:apps/spa_osprey/src/pages/configuration/hmrc-itsa/hmrc-itsa-form.jsx.
export const QuarterlySubmissionModule = () => {
  const {
    getBusinessesByNationalInsuranceNumber,
    getBusinessExtended,
    getSelfEmploymentBusinessObligations,
    calculateSelfEmploymentCumulativePeriodSummary,
    createSelfEmploymentCumulativePeriodSummary
  } = useHMRCITSAStore()
  // Estate pattern (see CaseTypeMappingModule): self-supplied top clearance when the layout
  // hides the PageTitleBlock on reduced-height windows (the breadcrumb bar is position:fixed).
  const { pageTitleBlockIsVisible } = useGlobalSettingsStore()
  const navigate = useNavigate()

  const [step, setStep] = useState(STEP_NI)
  const [nationalInsuranceNumber, setNationalInsuranceNumber] = useState('')
  const [accountingBasis, setAccountingBasis] = useState<AccountingBasis>('Accruals')
  const [businesses, setBusinesses] = useState<Business[]>([])
  const [selectedBusinessId, setSelectedBusinessId] = useState('')
  const [obligationStatusFilter, setObligationStatusFilter] = useState<ObligationStatus | ''>('')
  const [obligations, setObligations] = useState<ItsaBusinessObligation[]>([])
  const [selectedObligationIndex, setSelectedObligationIndex] = useState('')
  const [calculatedSummary, setCalculatedSummary] = useState<CalculateSelfEmploymentCumulativePeriodSummaryResponse | null>(
    null
  )
  const [isLoading, setIsLoading] = useState(false)
  const [tokenExpired, setTokenExpired] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [eligibilityError, setEligibilityError] = useState('')
  const [acknowledged, setAcknowledged] = useState(false)
  const [fraudPreventionNoticeAccepted, setFraudPreventionNoticeAccepted] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState('')
  const [submissionSucceeded, setSubmissionSucceeded] = useState(false)
  // RF14 (FPH-ACC11/FE6): non-prod fraud-header validation feedback carried on the submit
  // response — null in production (server-side guard), non-null in non-prod only.
  const [fraudHeaderValidation, setFraudHeaderValidation] = useState<FraudHeaderValidationSummary | null>(null)

  const isValidNumber = isValidNI(nationalInsuranceNumber)
  const selectedBusiness = businesses.find((business) => business.businessId === selectedBusinessId) || null
  const selectedObligation =
    selectedObligationIndex !== '' ? obligations[Number(selectedObligationIndex)] : null

  const handleReconnect = () => {
    navigate(HMRC_SETUP_ROUTE_PATH, { state: { tokenExpired: true } })
  }

  const loadObligations = async (filter: ObligationStatus | '') => {
    setErrorMessage('')
    setSelectedObligationIndex('')
    setIsLoading(true)
    try {
      const result = await getSelfEmploymentBusinessObligations({
        nationalInsuranceNumber: nationalInsuranceNumber.toUpperCase(),
        businessId: selectedBusinessId,
        obligationStatus: filter || undefined
      })
      setObligations((result as ItsaBusinessObligation[]) || [])
    } catch (error) {
      setObligations([])
      if (error instanceof HmrcTokenExpiredError) setTokenExpired(true)
      else setErrorMessage(getApiErrorMessage(error, GENERIC_OBLIGATIONS_ERROR))
    } finally {
      setIsLoading(false)
    }
  }

  const handleNiNext = async () => {
    setErrorMessage('')
    setTokenExpired(false)
    setIsLoading(true)
    try {
      const result = await getBusinessesByNationalInsuranceNumber({
        nationalInsuranceNumber: nationalInsuranceNumber.toUpperCase()
      })
      if (result && result.length > 0) {
        setBusinesses(result as Business[])
        setStep(STEP_BUSINESS)
      } else {
        setErrorMessage('No self-employment businesses were found for this National Insurance number.')
      }
    } catch (error) {
      if (error instanceof HmrcTokenExpiredError) setTokenExpired(true)
      else setErrorMessage(getApiErrorMessage(error, GENERIC_BUSINESSES_ERROR))
    } finally {
      setIsLoading(false)
    }
  }

  const handleBusinessBack = () => {
    setErrorMessage('')
    setEligibilityError('')
    setStep(STEP_NI)
  }

  // BE4's ItsaFirmEligibilityGuard runs on GetBusinessExtended and rejects with a 400
  // ValidationException for a non-self-employment firm type — surfaced here as an inline
  // validation message distinct from the generic error banner, and it blocks progression.
  const handleBusinessNext = async () => {
    setErrorMessage('')
    setEligibilityError('')
    setTokenExpired(false)
    setIsLoading(true)
    try {
      await getBusinessExtended({
        nationalInsuranceNumber: nationalInsuranceNumber.toUpperCase(),
        businessId: selectedBusinessId
      })
      setStep(STEP_OBLIGATION)
      await loadObligations(obligationStatusFilter)
    } catch (error) {
      if (error instanceof HmrcTokenExpiredError) setTokenExpired(true)
      else if (isValidationError(error)) setEligibilityError(getApiErrorMessage(error, GENERIC_ELIGIBILITY_ERROR))
      else setErrorMessage(getApiErrorMessage(error, GENERIC_ELIGIBILITY_ERROR))
    } finally {
      setIsLoading(false)
    }
  }

  const handleObligationBack = () => {
    setErrorMessage('')
    setStep(STEP_BUSINESS)
  }

  const handleObligationFilterChange = (newFilter: ObligationStatus | '') => {
    setObligationStatusFilter(newFilter)
    loadObligations(newFilter)
  }

  const handleObligationNext = async () => {
    if (!selectedObligation) return

    setErrorMessage('')
    setIsLoading(true)
    try {
      const result = await calculateSelfEmploymentCumulativePeriodSummary({
        nationalInsuranceNumber: nationalInsuranceNumber.toUpperCase(),
        businessId: selectedBusinessId,
        periodStartDate: selectedObligation.periodStartDate,
        periodEndDate: selectedObligation.periodEndDate,
        accountingBasis
      })
      setCalculatedSummary(result as CalculateSelfEmploymentCumulativePeriodSummaryResponse)
      // A fresh calculation mints a fresh acknowledgementToken bound to these figures —
      // reset the acknowledgement/submit state so the previous gate cannot carry over.
      setAcknowledged(false)
      setFraudPreventionNoticeAccepted(false)
      setSubmitError('')
      setSubmissionSucceeded(false)
      setStep(STEP_REVIEW)
    } catch (error) {
      setErrorMessage(getApiErrorMessage(error, GENERIC_CALCULATE_ERROR))
    } finally {
      setIsLoading(false)
    }
  }

  const handleReviewBack = () => {
    setErrorMessage('')
    setSubmitError('')
    setAcknowledged(false)
    setFraudPreventionNoticeAccepted(false)
    setStep(STEP_OBLIGATION)
  }

  // ADR 0031 (Model 2): the acknowledgementToken received from Calculate (BE9) is threaded
  // straight through to the submit — never fabricated or omitted; it is what BE6 verifies
  // against the figures being filed. userAcknowledged mirrors the checkbox (audit only).
  const handleSubmit = async () => {
    if (!calculatedSummary || !acknowledged || !fraudPreventionNoticeAccepted || isSubmitting) return

    setSubmitError('')
    setIsSubmitting(true)
    try {
      const response = await createSelfEmploymentCumulativePeriodSummary({
        nationalInsuranceNumber: nationalInsuranceNumber.toUpperCase(),
        businessId: selectedBusinessId,
        taxYear: deriveTaxYear(calculatedSummary.summary.periodDates.periodEndDate),
        cumulativePeriodSummary: calculatedSummary.summary,
        userAcknowledged: true,
        acknowledgementToken: calculatedSummary.acknowledgementToken
      })
      // BE6 returns CreateSelfEmploymentSubmissionResponse { success, fraudHeaderValidation }
      // (RF14/FPH-ACC11) — a false `success` means HMRC rejected the submission (the failed
      // attempt is still persisted as an audit row server-side, BE2). fraudHeaderValidation is
      // surfaced via the toast regardless of the success outcome (RF14: it never blocks or
      // otherwise affects the submit flow).
      const success = response && typeof response === 'object' ? response.success : response
      const validation = response && typeof response === 'object' ? response.fraudHeaderValidation : null
      setFraudHeaderValidation(validation || null)
      if (success === false) setSubmitError(GENERIC_SUBMIT_ERROR)
      else setSubmissionSucceeded(true)
    } catch (error) {
      if (error instanceof HmrcTokenExpiredError) setTokenExpired(true)
      else setSubmitError(getApiErrorMessage(error, GENERIC_SUBMIT_ERROR))
    } finally {
      setIsSubmitting(false)
    }
  }

  const renderLoadingOverlay = () => {
    if (!isLoading) return null
    return (
      <MUIStack alignItems="center" sx={{ padding: 4 }} data-testid="QuarterlySubmissionModuleLoading">
        <MUICircularProgress />
      </MUIStack>
    )
  }

  const renderNiStep = () => (
    <>
      <MUITypography variant="h6">Enter the National Insurance number</MUITypography>
      <MUITypography variant="body1">
        We will look up the self-employment businesses linked to this National Insurance number via HMRC.
      </MUITypography>

      <NINumberField value={nationalInsuranceNumber} onChange={setNationalInsuranceNumber} required disabled={isLoading} />

      <MUIStack gap={1}>
        <MUITypography variant="subtitle2" sx={{ fontWeight: 700 }}>
          Accounting basis
        </MUITypography>
        <MUIRadioGroup
          row
          value={accountingBasis}
          onChange={(event) => setAccountingBasis(event.target.value as AccountingBasis)}
        >
          <MUIFormControlLabel value="Accruals" control={<MUIRadio />} label="Accruals" disabled={isLoading} />
          <MUIFormControlLabel value="Cash" control={<MUIRadio />} label="Cash" disabled={isLoading} />
        </MUIRadioGroup>
      </MUIStack>

      {renderLoadingOverlay()}

      <MUIStack direction="row" justifyContent="flex-end">
        <MUIButton
          variant="contained"
          color="primary"
          size="large"
          data-testid="QuarterlySubmissionModule-niNextButton"
          onClick={handleNiNext}
          disabled={!isValidNumber || isLoading}
          sx={{ maxWidth: 200 }}
        >
          {isLoading ? <MUICircularProgress size={24} sx={{ color: 'white' }} /> : 'Next'}
        </MUIButton>
      </MUIStack>
    </>
  )

  const renderBusinessStep = () => (
    <>
      <MUITypography variant="h6">Select a business</MUITypography>
      <MUITypography variant="body1">
        The following self-employment businesses are linked to this National Insurance number.
      </MUITypography>

      {eligibilityError && (
        <MUIAlert severity="error" data-testid="QuarterlySubmissionModuleEligibilityError">
          {eligibilityError}
        </MUIAlert>
      )}

      {isLoading ? (
        renderLoadingOverlay()
      ) : (
        <MUIRadioGroup value={selectedBusinessId} onChange={(event) => setSelectedBusinessId(event.target.value)}>
          {businesses.map((business) => (
            <MUIFormControlLabel
              key={business.businessId}
              value={business.businessId}
              control={<MUIRadio />}
              data-testid={`QuarterlySubmissionModule-businessOption-${business.businessId}`}
              label={
                <MUIStack sx={{ padding: '8px 0' }}>
                  <MUITypography variant="body1" sx={{ fontWeight: 700 }}>
                    {business.tradingName || 'Unnamed business'}
                  </MUITypography>
                  <MUITypography variant="body2" sx={{ color: 'text.secondary' }}>
                    Type: {business.typeOfBusiness} | ID: {business.businessId}
                  </MUITypography>
                </MUIStack>
              }
              sx={optionBoxSx}
            />
          ))}
        </MUIRadioGroup>
      )}

      <MUIStack direction="row" justifyContent="space-between">
        <MUIButton variant="outlined" size="large" onClick={handleBusinessBack} disabled={isLoading} sx={{ maxWidth: 120 }}>
          Back
        </MUIButton>
        <MUIButton
          variant="contained"
          color="primary"
          size="large"
          data-testid="QuarterlySubmissionModule-businessNextButton"
          onClick={handleBusinessNext}
          disabled={!selectedBusinessId || isLoading}
          sx={{ maxWidth: 200 }}
        >
          {isLoading ? <MUICircularProgress size={24} sx={{ color: 'white' }} /> : 'Next'}
        </MUIButton>
      </MUIStack>
    </>
  )

  const renderObligationStep = () => (
    <>
      <MUITypography variant="h6">Select an obligation</MUITypography>
      <MUITypography variant="body1">
        Select an obligation period for {selectedBusiness?.tradingName || 'the selected business'}.
      </MUITypography>

      <MUIFormControl sx={{ maxWidth: 250 }}>
        <MUIInputLabel id="obligation-status-filter-label">Filter by status</MUIInputLabel>
        <MUISelect
          labelId="obligation-status-filter-label"
          label="Filter by status"
          data-testid="QuarterlySubmissionModule-obligationStatusFilter"
          value={obligationStatusFilter}
          onChange={(event) => handleObligationFilterChange(event.target.value as ObligationStatus | '')}
          disabled={isLoading}
        >
          {OBLIGATION_STATUS_FILTER_OPTIONS.map((option) => (
            <MUIMenuItem key={option.value} value={option.value}>
              {option.label}
            </MUIMenuItem>
          ))}
        </MUISelect>
      </MUIFormControl>

      {isLoading ? (
        renderLoadingOverlay()
      ) : obligations.length === 0 ? (
        <MUITypography variant="body2" sx={{ color: 'text.secondary' }}>
          No obligations found for this business.
        </MUITypography>
      ) : (
        <MUIRadioGroup value={selectedObligationIndex} onChange={(event) => setSelectedObligationIndex(event.target.value)}>
          {obligations.map((obligation, index) => (
            <MUIFormControlLabel
              key={`${obligation.businessId}-${obligation.periodStartDate}-${obligation.periodEndDate}`}
              value={String(index)}
              control={<MUIRadio />}
              data-testid={`QuarterlySubmissionModule-obligationOption-${index}`}
              label={
                <MUIStack sx={{ padding: '8px 0' }}>
                  <MUITypography variant="body1" sx={{ fontWeight: 700 }}>
                    {obligation.periodStartDate} to {obligation.periodEndDate}
                  </MUITypography>
                  <MUITypography variant="body2" sx={{ color: 'text.secondary' }}>
                    Due: {obligation.dueDate} | Status: {obligation.status}
                    {obligation.receivedDate && ` | Received: ${obligation.receivedDate}`}
                  </MUITypography>
                </MUIStack>
              }
              sx={optionBoxSx}
            />
          ))}
        </MUIRadioGroup>
      )}

      <MUIStack direction="row" justifyContent="space-between">
        <MUIButton
          variant="outlined"
          size="large"
          onClick={handleObligationBack}
          disabled={isLoading}
          sx={{ maxWidth: 120 }}
        >
          Back
        </MUIButton>
        <MUIButton
          variant="contained"
          color="primary"
          size="large"
          data-testid="QuarterlySubmissionModule-obligationNextButton"
          onClick={handleObligationNext}
          disabled={!selectedObligation || isLoading}
          sx={{ maxWidth: 200 }}
        >
          {isLoading ? <MUICircularProgress size={24} sx={{ color: 'white' }} /> : 'Calculate'}
        </MUIButton>
      </MUIStack>
    </>
  )

  const renderReviewStep = () => {
    if (!calculatedSummary) return null
    const { summary } = calculatedSummary
    const taxYear = deriveTaxYear(summary.periodDates.periodEndDate)

    return (
      <>
        <MUITypography variant="h6">Review the calculated figures</MUITypography>
        <MUIAlert severity="info" sx={{ py: 0.5 }} data-testid="QuarterlySubmissionModuleReviewNotice">
          These figures are calculated from your Osprey ledger and are read-only at this step.
        </MUIAlert>

        <MUIStack direction="row" gap={2} flexWrap="wrap" alignItems="center">
          <MUITypography variant="body2">
            <strong>Business:</strong> {selectedBusiness?.tradingName || selectedBusinessId}
          </MUITypography>
          <MUITypography variant="body2">
            <strong>Tax year:</strong> {taxYear}
          </MUITypography>
          <MUITypography variant="body2">
            <strong>Period:</strong> {summary.periodDates.periodStartDate} to {summary.periodDates.periodEndDate}
          </MUITypography>
          <AccountingBasisPill accountingBasis={accountingBasis} />
        </MUIStack>

        <FiguresReviewTable title="Period income" rows={buildFigureRows(INCOME_FIELDS, summary.periodIncome)} />
        <FiguresReviewTable title="Period expenses" rows={buildFigureRows(EXPENSE_FIELDS, summary.periodExpenses)} />
        <FiguresReviewTable
          title="Period disallowable expenses"
          rows={buildFigureRows(DISALLOWABLE_EXPENSE_FIELDS, summary.periodDisallowableExpenses)}
        />

        {accountingBasis === 'Cash' && (
          <MUIAlert severity="warning" data-testid="QuarterlySubmissionModuleCashBasisWarning">
            {CASH_BASIS_WARNING}
          </MUIAlert>
        )}

        {submitError && (
          <MUIAlert severity="error" data-testid="QuarterlySubmissionModuleSubmitError">
            {submitError}
          </MUIAlert>
        )}

        <MUIFormControlLabel
          control={
            <MUICheckbox
              checked={acknowledged}
              onChange={(event) => setAcknowledged(event.target.checked)}
              disabled={isSubmitting}
              inputProps={{ 'data-testid': 'QuarterlySubmissionModule-acknowledgeCheckbox' }}
            />
          }
          label={ACKNOWLEDGEMENT_TEXT}
        />

        <MUIFormControlLabel
          control={
            <MUICheckbox
              checked={fraudPreventionNoticeAccepted}
              onChange={(event) => setFraudPreventionNoticeAccepted(event.target.checked)}
              disabled={isSubmitting}
              inputProps={{ 'data-testid': 'QuarterlySubmissionModule-fraudPreventionNoticeCheckbox' }}
            />
          }
          label={FRAUD_PREVENTION_NOTICE_TEXT}
        />

        <MUIStack direction="row" justifyContent="space-between">
          <MUIButton
            variant="outlined"
            size="large"
            onClick={handleReviewBack}
            disabled={isSubmitting}
            sx={{ maxWidth: 120 }}
          >
            Back
          </MUIButton>
          <MUIButton
            variant="contained"
            color="primary"
            size="large"
            data-testid="QuarterlySubmissionModule-submitButton"
            onClick={handleSubmit}
            disabled={!acknowledged || !fraudPreventionNoticeAccepted || isSubmitting}
            sx={{ maxWidth: 240 }}
          >
            {isSubmitting ? <MUICircularProgress size={24} sx={{ color: 'white' }} /> : 'Submit to HMRC'}
          </MUIButton>
        </MUIStack>
      </>
    )
  }

  const renderReceipt = () => {
    if (!calculatedSummary) return null
    const { summary } = calculatedSummary
    const taxYear = deriveTaxYear(summary.periodDates.periodEndDate)

    return (
      <MUIStack gap={2} data-testid="QuarterlySubmissionModuleReceipt">
        <MUIAlert severity="success">Your quarterly update has been submitted to HMRC.</MUIAlert>
        <MUITypography variant="h6">Submission receipt</MUITypography>
        <MUITypography variant="body2">
          <strong>Business:</strong> {selectedBusiness?.tradingName || selectedBusinessId}
        </MUITypography>
        <MUITypography variant="body2">
          <strong>Tax year:</strong> {taxYear}
        </MUITypography>
        <MUITypography variant="body2">
          <strong>Period:</strong> {summary.periodDates.periodStartDate} to {summary.periodDates.periodEndDate}
        </MUITypography>
        <MUITypography variant="body2">
          <strong>Submission type:</strong> Cumulative period summary
        </MUITypography>
      </MUIStack>
    )
  }

  return (
    <MUIStack
      data-testid="QuarterlySubmissionModule"
      gap={4}
      sx={{
        maxWidth: 720,
        margin: 'auto',
        mt: pageTitleBlockIsVisible ? 'auto' : '12rem',
        backgroundColor: 'white',
        borderRadius: '6px',
        px: '40px',
        pb: '60px',
        pt: '40px'
      }}
    >
      <StepIndicator steps={STEPS} currentStepIndex={step} />

      {tokenExpired && (
        <MUIAlert
          severity="warning"
          data-testid="QuarterlySubmissionModuleTokenExpiredBanner"
          action={
            <MUIButton
              color="inherit"
              size="small"
              data-testid="QuarterlySubmissionModuleReconnectButton"
              onClick={handleReconnect}
            >
              Reconnect to HMRC
            </MUIButton>
          }
        >
          Your HMRC access has expired. Please reconnect your account to continue.
        </MUIAlert>
      )}

      {!tokenExpired && errorMessage && (
        <MUIAlert severity="error" data-testid="QuarterlySubmissionModuleError">
          {errorMessage}
        </MUIAlert>
      )}

      {!tokenExpired && step === STEP_NI && renderNiStep()}
      {!tokenExpired && step === STEP_BUSINESS && renderBusinessStep()}
      {!tokenExpired && step === STEP_OBLIGATION && renderObligationStep()}
      {!tokenExpired && step === STEP_REVIEW && (submissionSucceeded ? renderReceipt() : renderReviewStep())}

      <FraudHeaderValidationToast validation={fraudHeaderValidation} />
    </MUIStack>
  )
}
